/**
 * Service para gerenciar lógica de afiliados no checkout
 * 
 * Responsabilidades:
 * - Capturar parâmetro affh da URL
 * - Gerenciar cookies de afiliado
 * - Aplicar modelos de atribuição (first_click, last_click)
 * - Preparar dados de afiliado para requisições
 */

const COOKIE_NAME = 'affh';
const DEFAULT_COOKIE_DURATION = 7; // dias

/**
 * Obtém o valor de um cookie pelo nome
 * @param {string} name - Nome do cookie
 * @returns {string|null} - Valor do cookie ou null
 */
export function getCookie(name) {
    if (typeof document === 'undefined') return null;
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    
    if (parts.length === 2) {
        return parts.pop().split(';').shift();
    }
    
    return null;
}

/**
 * Define um cookie
 * @param {string} name - Nome do cookie
 * @param {string} value - Valor do cookie
 * @param {number} days - Duração em dias
 */
export function setCookie(name, value, days = DEFAULT_COOKIE_DURATION) {
    if (typeof document === 'undefined') return;
    
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    
    // Adiciona Secure quando em HTTPS (obrigatório para persistência em browsers modernos)
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    
    document.cookie = `${name}=${value}; ${expires}; path=/; SameSite=Lax${secureFlag}`;
}

/**
 * Remove um cookie
 * @param {string} name - Nome do cookie
 */
export function removeCookie(name) {
    if (typeof document === 'undefined') return;
    
    // Adiciona Secure quando em HTTPS para garantir que o cookie seja removido corretamente
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secureFlag}`;
}

/**
 * Classe principal do serviço de afiliados
 */
export class AffiliateService {
    /**
     * Processa configurações de afiliado vindas da API
     * @param {Object} data - Dados da resposta da API (deve conter affiliate e affiliates_config)
     * @param {Object} route - Objeto route do Vue Router ($route)
     * @returns {string|null} - Hash do afiliado processado
     */
    static processAffiliateConfiguration(data, route) {
        // Se não há dados de afiliado, retorna null
        if (!data.affiliate && !data.affiliates_config) {
            return getCookie(COOKIE_NAME) || null;
        }

        let affh = null;

        // Verifica se há parâmetro affh na URL
        if (route.query.affh && route.query.affh !== '') {
            affh = this.handleAffiliateQueryParam(data, route);
        }

        // Se não definiu affh via query param, tenta pegar do cookie
        if (!affh && getCookie(COOKIE_NAME)) {
            affh = getCookie(COOKIE_NAME);
        }

        return affh;
    }

    /**
     * Processa parâmetro de query affh
     * @param {Object} data - Dados da resposta da API
     * @param {Object} route - Objeto route do Vue Router
     * @returns {string|null} - Hash do afiliado processado
     */
    static handleAffiliateQueryParam(data, route) {
        // Valida se o afiliado é válido
        if (!data.affiliates_config) {
            return null;
        }

        const attributionModel = data.affiliates_config?.attribuition_model || 'last_click';
        const cookieDuration = data.affiliates_config?.cookie_duration || DEFAULT_COOKIE_DURATION;

        switch (attributionModel) {
            case 'last_click':
                return this.handleLastClickAttribution(route, cookieDuration);

            case 'first_click':
                return this.handleFirstClickAttribution(route, cookieDuration);

            default:
                return this.handleLastClickAttribution(route, cookieDuration);
        }
    }

    /**
     * Processa atribuição de último clique
     * Sempre sobrescreve o cookie existente
     * @param {Object} route - Objeto route do Vue Router
     * @param {number} cookieDuration - Duração do cookie em dias
     * @returns {string} - Hash do afiliado
     */
    static handleLastClickAttribution(route, cookieDuration) {
        const affh = route.query.affh;
        
        // Remove cookie existente e define novo
        removeCookie(COOKIE_NAME);
        setCookie(COOKIE_NAME, affh, cookieDuration);
        
        return affh;
    }

    /**
     * Processa atribuição de primeiro clique
     * Mantém o cookie existente se já houver
     * @param {Object} route - Objeto route do Vue Router
     * @param {number} cookieDuration - Duração do cookie em dias
     * @returns {string} - Hash do afiliado
     */
    static handleFirstClickAttribution(route, cookieDuration) {
        const existingAffh = getCookie(COOKIE_NAME);
        
        // Se já existe um cookie, mantém ele (primeiro clique)
        if (existingAffh) {
            return existingAffh;
        }
        
        // Caso contrário, define o novo
        const affh = route.query.affh;
        setCookie(COOKIE_NAME, affh, cookieDuration);
        
        return affh;
    }

    /**
     * Obtém o hash do afiliado atual
     * Prioriza query param, depois cookie
     * @param {Object} route - Objeto route do Vue Router
     * @returns {string|null} - Hash do afiliado ou null
     */
    static getCurrentAffiliateHash(route) {
        // Prioriza query param
        if (route?.query?.affh && route.query.affh !== '') {
            return route.query.affh;
        }
        
        // Fallback para cookie
        return getCookie(COOKIE_NAME);
    }

    /**
     * Prepara parâmetros de afiliado para requisições
     * @param {Object} route - Objeto route do Vue Router
     * @returns {Object} - Objeto com affh e insert_aff para requisições
     */
    static prepareAffiliateParams(route) {
        const affh = this.getCurrentAffiliateHash(route);
        const insertAff = affh ? `?affh=${affh}` : '';

        return { 
            affh, 
            insertAff,
            affiliateHash: affh // alias para uso no payload
        };
    }

    /**
     * Adiciona parâmetro de afiliado a uma URL
     * @param {string} url - URL base
     * @param {Object} route - Objeto route do Vue Router
     * @returns {string} - URL com parâmetro de afiliado
     */
    static appendAffiliateToUrl(url, route) {
        const affh = this.getCurrentAffiliateHash(route);
        
        if (!affh) {
            return url;
        }

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}affh=${affh}`;
    }

    /**
     * Limpa dados de afiliado (cookie)
     */
    static clearAffiliateData() {
        removeCookie(COOKIE_NAME);
    }

    /**
     * Verifica se há um afiliado ativo
     * @param {Object} route - Objeto route do Vue Router (opcional)
     * @returns {boolean}
     */
    static hasActiveAffiliate(route = null) {
        if (route?.query?.affh) {
            return true;
        }
        
        return !!getCookie(COOKIE_NAME);
    }
}

export default AffiliateService;

